/**
 * Os rostos de com quem um artefato está compartilhado.
 *
 * ## Por que isto mora em `components/` e não na tela que o criou
 *
 * Nasceu dentro de `pages/Sites.jsx`, para o rodapé do cartão. Quando a aba de
 * moderação passou a mostrar a mesma coisa na coluna "Quem vê", o componente de
 * administração acabou importando de uma **página** — uma peça reutilizável
 * pendurada numa tela, que arrasta junto tudo o que aquela tela importa e não
 * dá pista nenhuma de que é compartilhada.
 *
 * Dois lugares já são o bastante para ser peça própria. Um terceiro vai
 * aparecer: a faixa de `SiteDetail` e a do conteúdo publicado (servida pelo
 * Express, sem React) mostram a mesma informação com os mesmos efeitos.
 */
import { photoUrl } from '../api.js';
import { initialsOf } from './AppShell.jsx';
import { Avatar } from './Avatar.jsx';

/** Quantos rostos cabem no rodapé do cartão antes do `+N`. */
const ROSTOS_NO_CARTAO = 4;

/** `Nome · e-mail`, ou só o e-mail de quem ainda não entrou no AI Hub. */
export const comoSeLe = (p) => (p.name ? `${p.name} · ${p.upn}` : p.upn);

/**
 * Mostra quem cabe e resume o resto em `+N`, com os nomes que vieram no
 * `title`. O servidor manda só os primeiros de cada artefato — a grade não
 * precisa de duzentas pessoas para desenhar quatro rostos.
 *
 * Sem `oid` não há foto: é quem ainda não entrou no AI Hub, e o `Avatar` cai
 * nas iniciais sozinho quando `src` é nulo.
 *
 * `total` vem separado de `lista` **de propósito**: a lista é a amostra, e o
 * `+N` precisa do número cheio. Onde a amostra já é tudo — a moderação, que
 * recebe o recorte inteiro —, passar `lista.length` é o certo.
 */
export function RostosDoCartao({ lista, total }) {
  if (!total) return null;

  const mostrados = (lista ?? []).slice(0, ROSTOS_NO_CARTAO);
  const restam = total - mostrados.length;
  const sobram = (lista ?? []).slice(ROSTOS_NO_CARTAO).map(comoSeLe);

  return (
    <span className="site-rostos">
      {mostrados.map((p) => (
        <span key={p.upn} className="site-rosto" title={comoSeLe(p)}>
          <Avatar
            src={p.oid ? photoUrl.usuario(p.oid) : null}
            initials={initialsOf(p.name, p.upn)}
            name={p.name}
            size="sm"
          />
        </span>
      ))}

      {restam > 0 && (
        <span
          className="site-rosto site-rosto-mais"
          title={sobram.length ? sobram.join('\n') : `mais ${restam}`}
        >
          +{restam}
        </span>
      )}
    </span>
  );
}

export default RostosDoCartao;
